// src/pages/messages/InboxV2.jsx
//
// Chat v2 Inbox: lists all chats the user is a participant in,
// ordered by most recent message. Phase 2 of the chat rebuild.
//
// Phase 3 will build the chat detail page (`/messages/:chatId`)
// where messages actually render. For now, clicking a row routes
// to the placeholder.

import React, { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'
import useChats from '@/hooks/useChats'
import useSmsReplyUnread from '@/hooks/useSmsReplyUnread'
import { mergeInboxRows, combinedUnreadCount } from '@/hooks/smsReplyHelpers'
import SmsReplyRow from './SmsReplyRow'
import { fmtRelative } from './relativeTime'
import {
  ChatBubbleLeftRightIcon,
  PlusIcon,
  MegaphoneIcon,
  UserIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline'

const HEAD_FONT = "'Barlow Condensed', sans-serif"

const FILTERS = [
  { id: 'all',    label: 'All' },
  { id: 'unread', label: 'Unread' },
]

function ChatAvatar({ chat }) {
  // Team announcement: lime icon container with megaphone
  // DM: zinc with user icon
  const isTeam = chat.type === 'team_announcement'

  if (chat.photoUrl) {
    return (
      <img
        src={chat.photoUrl}
        alt=""
        className="h-12 w-12 rounded-2xl object-cover ring-1 ring-zinc-200 flex-shrink-0"
      />
    )
  }

  if (isTeam) {
    return (
      <div className="h-12 w-12 rounded-2xl bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40 flex items-center justify-center flex-shrink-0">
        <MegaphoneIcon className="h-6 w-6 text-[#5a8a1f]" />
      </div>
    )
  }

  return (
    <div className="h-12 w-12 rounded-2xl bg-zinc-100 ring-1 ring-zinc-200 flex items-center justify-center flex-shrink-0">
      <UserIcon className="h-6 w-6 text-zinc-500" />
    </div>
  )
}

function ChatRow({ chat, currentUid }) {
  const last = chat.lastMessage
  const previewText = last?.text || (chat.type === 'team_announcement'
    ? 'No announcements yet'
    : 'Say hi to start the conversation')
  const previewSender = last?.senderId
    ? (chat.type === 'team_announcement'
        ? `${last.senderName}: `
        : '')
    : ''

  // For DMs, the chat name + photo should reflect the OTHER
  // participant from the current user's POV. The stored
  // chat.name works for the creator but not the recipient,
  // so we always derive dynamically here.
  let displayName = chat.name || 'Untitled'
  let displayPhoto = chat.photoUrl
  if (chat.type === 'dm' && currentUid) {
    const otherUid = (chat.participantIds || []).find((p) => p !== currentUid)
    const otherMember = chat.members?.[otherUid]
    if (otherMember) {
      displayName = otherMember.displayName || displayName
      displayPhoto = otherMember.photoURL || displayPhoto
    }
  }

  return (
    <Link
      to={`/messages/${chat.id}`}
      className={[
        'block px-4 py-3 border-b border-zinc-100 hover:bg-[#f7faf5] transition-colors',
        chat.isUnread ? 'bg-[#b5f03d]/5' : 'bg-white',
      ].join(' ')}
    >
      <div className="flex items-start gap-3">
        <ChatAvatar chat={{ ...chat, photoUrl: displayPhoto }} />

        <div className="flex-1 min-w-0">
          {/* Top row: name + time */}
          <div className="flex items-center justify-between gap-2 mb-0.5">
            <p className={[
              'text-sm truncate',
              chat.isUnread ? 'font-bold text-[#0d1f14]' : 'font-semibold text-[#0d1f14]',
            ].join(' ')}>
              {displayName}
              {chat.type === 'team_announcement' && (
                <span
                  className="ml-2 inline-flex items-center gap-0.5 rounded bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/30 text-[#5a8a1f] px-1.5 py-px text-[9px] font-bold uppercase align-middle"
                  style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                >
                  Team
                </span>
              )}
            </p>
            <span className={[
              'text-[11px] flex-shrink-0',
              chat.isUnread ? 'text-[#5a8a1f] font-semibold' : 'text-zinc-400',
            ].join(' ')}>
              {fmtRelative(last?.sentAt || chat.lastMessageAt || chat.createdAt)}
            </span>
          </div>

          {/* Preview */}
          <div className="flex items-center justify-between gap-2">
            <p className={[
              'text-xs truncate',
              chat.isUnread ? 'text-[#0d1f14] font-medium' : 'text-zinc-500',
            ].join(' ')}>
              {previewSender}{previewText}
            </p>
            {chat.isUnread && (
              <span
                aria-label="Unread"
                className="h-2 w-2 rounded-full bg-[#b5f03d] flex-shrink-0 ring-2 ring-[#b5f03d]/30"
              />
            )}
          </div>
        </div>
      </div>
    </Link>
  )
}

function EmptyState({ filter }) {
  return (
    <div className="text-center py-20 px-6">
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40 mb-5">
        <ChatBubbleLeftRightIcon className="h-8 w-8 text-[#5a8a1f]" />
      </div>
      <h3
        className="text-xl font-bold uppercase text-[#0d1f14] mb-2"
        style={{ fontFamily: HEAD_FONT }}
      >
        {filter === 'unread' ? 'All Caught Up' : 'No Messages Yet'}
      </h3>
      <p className="text-sm text-zinc-500 max-w-sm mx-auto">
        {filter === 'unread'
          ? 'Nothing unread. Switch back to All to see your conversations.'
          : 'Your team announcement channel and any direct messages will appear here.'}
      </p>
    </div>
  )
}

// Hint card for the top of a populated inbox. Surfaces team
// channels with a "TEAM" pill explanation so coaches understand
// the megaphone-icon row IS where they message everyone.
function TeamChannelHint({ teamChats }) {
  if (teamChats.length === 0) return null
  return (
    <div className="mb-4 rounded-xl border border-[#b5f03d]/30 bg-[#b5f03d]/5 px-4 py-3 flex items-start gap-3">
      <MegaphoneIcon className="h-5 w-5 text-[#5a8a1f] flex-shrink-0 mt-0.5" />
      <div className="flex-1 text-xs text-zinc-600 leading-relaxed">
        <span className="font-bold text-[#0d1f14]">Team channels</span> {teamChats.length === 1 ? 'is' : 'are'} where you message your whole team. Look for the{' '}
        <span
          className="inline-flex items-center gap-0.5 rounded bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/30 text-[#5a8a1f] px-1.5 py-px text-[9px] font-bold uppercase align-middle"
          style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
        >
          Team
        </span>{' '}
        pill. Tap to post.
      </div>
    </div>
  )
}

export default function InboxV2() {
  const { firebaseUser } = useAuth()
  const { chats, loading, error } = useChats(firebaseUser?.uid)
  const sms = useSmsReplyUnread()
  const [filter, setFilter] = useState('all')

  // One merged, recency-sorted list of chat rows and SMS-reply rows
  // (features/unified-replies/, BA D-4). SMS rows are unread by
  // definition (they only exist while unread replies exist), so the
  // unread filter includes them unconditionally.
  const rows = useMemo(() => mergeInboxRows(chats, sms.perTeam), [chats, sms.perTeam])

  const visible = useMemo(() => {
    if (filter === 'unread') return rows.filter((r) => r.kind === 'sms' || r.chat.isUnread)
    return rows
  }, [rows, filter])

  // Combined number (unread chats + teams with unread SMS replies) so
  // the chip always matches the global badge (BA D-2, AC-2/AC-8).
  const unreadCount = useMemo(
    () => combinedUnreadCount(chats.filter((c) => c.isUnread).length, sms.units),
    [chats, sms.units]
  )

  // Team channels (the auto-created announcement channels) get
  // a one-line hint at the top so coaches understand which row
  // is "where I message the whole team."
  const teamChats = useMemo(() => chats.filter((c) => c.type === 'team_announcement'), [chats])

  return (
    <div className="min-h-screen bg-[#f7faf5]">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 sm:py-8">

        {/* Header */}
        <div className="flex items-start justify-between gap-3 mb-5">
          <div>
            <h1
              className="text-2xl font-bold uppercase tracking-tight text-[#0d1f14]"
              style={{ fontFamily: HEAD_FONT }}
            >
              Messages
            </h1>
            <p className="text-sm text-zinc-500 mt-1">
              Team announcements and direct messages.
            </p>
          </div>
          {/* New message button. Phase 5 will wire this to a DM
              composer; for now it routes to the rebuild placeholder
              so users see "coming soon" instead of a dead button. */}
          <Link
            to="/messages/new"
            className="inline-flex items-center gap-2 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-4 py-2.5 text-sm font-bold uppercase shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] transition-all active:scale-[0.97] flex-shrink-0"
            style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
          >
            <PlusIcon className="h-4 w-4" />
            <span className="hidden sm:inline">New Message</span>
            <span className="sm:hidden">New</span>
          </Link>
        </div>

        {/* Filter chips — keyed off the combined list so an inbox with
            only SMS rows still shows them (arch risk list) */}
        {rows.length > 0 && (
          <div className="flex rounded-xl border border-zinc-200 bg-white p-1 gap-1 mb-4 self-start w-fit">
            {FILTERS.map((tab) => {
              const active = tab.id === filter
              const showCount = tab.id === 'unread' && unreadCount > 0
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setFilter(tab.id)}
                  className={[
                    'px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap inline-flex items-center gap-1.5',
                    active
                      ? 'bg-[#b5f03d] text-[#0d1f14] shadow-sm'
                      : 'text-zinc-500 hover:text-[#0d1f14]',
                  ].join(' ')}
                >
                  {tab.label}
                  {showCount && (
                    <span className={[
                      'inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold',
                      active ? 'bg-[#0d1f14] text-[#b5f03d]' : 'bg-[#b5f03d]/20 text-[#5a8a1f]',
                    ].join(' ')}>
                      {unreadCount}
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        )}

        {/* List */}
        {loading ? (
          <div className="flex items-center justify-center py-20 text-zinc-400">
            <ArrowPathIcon className="h-6 w-6 animate-spin" />
          </div>
        ) : error ? (
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            Could not load messages: {error}
          </div>
        ) : visible.length === 0 ? (
          <EmptyState filter={filter} />
        ) : (
          <>
            {filter === 'all' && <TeamChannelHint teamChats={teamChats} />}
            <div className="rounded-2xl border border-zinc-200 bg-white shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] overflow-hidden">
              {visible.map((r) =>
                r.kind === 'sms' ? (
                  <SmsReplyRow
                    key={`sms-${r.sms.teamId}`}
                    teamId={r.sms.teamId}
                    name={r.sms.name}
                    count={r.sms.count}
                    newestAtMs={r.sms.newestAtMs}
                  />
                ) : (
                  <ChatRow key={r.chat.id} chat={r.chat} currentUid={firebaseUser?.uid} />
                )
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
